'use client';

import { type ComponentProps } from 'react';
import { Root, Trigger, Anchor, Portal, Content } from '@radix-ui/react-popover';

import { cn } from '@/util/utils';

function Popover({
    open,
    defaultOpen,
    onOpenChange,
    modal,
    ...props
}: ComponentProps<typeof Root>) {
    return (
        <Root
            open={open}
            defaultOpen={defaultOpen}
            onOpenChange={onOpenChange}
            modal={modal}
            data-slot="popover"
            {...props}
        />
    );
}

function PopoverTrigger({ asChild, ref, ...props }: ComponentProps<typeof Trigger>) {
    return <Trigger data-slot="popover-trigger" asChild={asChild} ref={ref} {...props} />;
}

function PopoverAnchor({ virtualRef, asChild, ref, ...props }: ComponentProps<typeof Anchor>) {
    return (
        <Anchor
            data-slot="popover-anchor"
            virtualRef={virtualRef}
            asChild={asChild}
            ref={ref}
            {...props}
        />
    );
}

function PopoverContent({
    className,
    align = 'center',
    // 6px off its field, as the prototype places its date popover and select panels.
    sideOffset = 6,
    forceMount,
    onOpenAutoFocus,
    onCloseAutoFocus,
    side,
    alignOffset,
    arrowPadding,
    avoidCollisions,
    collisionBoundary,
    collisionPadding,
    sticky,
    hideWhenDetached,
    updatePositionStrategy,
    onEscapeKeyDown,
    onPointerDownOutside,
    onFocusOutside,
    onInteractOutside,
    asChild,
    ref,
    ...props
}: ComponentProps<typeof Content>) {
    return (
        <Portal>
            <Content
                // The Calendar keys its transparent ground off this slot, so a calendar in a
                // popover shows the popover's white rather than the page canvas.
                data-slot="popover-content"
                forceMount={forceMount}
                ref={ref}
                align={align}
                sideOffset={sideOffset}
                onOpenAutoFocus={onOpenAutoFocus}
                onCloseAutoFocus={onCloseAutoFocus}
                side={side}
                alignOffset={alignOffset}
                arrowPadding={arrowPadding}
                avoidCollisions={avoidCollisions}
                collisionBoundary={collisionBoundary}
                collisionPadding={collisionPadding}
                sticky={sticky}
                hideWhenDetached={hideWhenDetached}
                updatePositionStrategy={updatePositionStrategy}
                onEscapeKeyDown={onEscapeKeyDown}
                onPointerDownOutside={onPointerDownOutside}
                onFocusOutside={onFocusOutside}
                onInteractOutside={onInteractOutside}
                asChild={asChild}
                // No taller than the room beside its trigger, scrolling inside past that, as a
                // select's panel does: a calendar with its time and Done is 425px, and on a phone
                // a field near the top of the screen left Done under the screen's edge (ALS-461).
                className={cn(
                    'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-(--radix-popover-content-available-height) w-72 origin-[--radix-popover-content-transform-origin] overflow-y-auto rounded-md border p-4 shadow-md outline-none',
                    className,
                )}
                {...props}
            />
        </Portal>
    );
}

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
